import React from 'react'
import { Alert, Button, Modal } from 'antd'
import { CheckCircleOutlined, EditOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import {
  DocumentDraft,
  ReviewDocument,
  ReviewLine,
  displayValue,
  draftForConfirmation,
  issueResolved,
  moneyValue,
  reviewCompletionState,
  rowHasDataQuestions,
  totalsCheck,
  valuesFor,
} from './result-model'

type ReviewConfirmationProps = {
  open: boolean
  completing: boolean
  document?: ReviewDocument
  draft: DocumentDraft | null
  saving: boolean
  readOnly: boolean
  error?: string
  resultChanged: boolean
  onChange: (update: (draft: DocumentDraft) => DocumentDraft) => void
  onClose: () => void
  onEditLine: (line: ReviewLine) => void
  onEditHeader: () => void
}
export default function ReviewConfirmationDialog(props: ReviewConfirmationProps) {
  return (
    <Modal
      className="supply-check__completion"
      title={props.completing ? 'Завершение проверки накладной' : 'Вопросы по накладной'}
      open={props.open}
      width={800}
      onCancel={props.onClose}
      closable={!props.saving}
      maskClosable={!props.saving}
      keyboard={!props.saving}
      footer={
        <Button disabled={props.saving} onClick={props.onClose}>
          Вернуться к накладной
        </Button>
      }
    >
      <ReviewConfirmationContent {...props} />
    </Modal>
  )
}
export function ReviewConfirmationContent({
  document,
  draft,
  saving,
  readOnly,
  error,
  resultChanged,
  onChange,
  onEditLine,
  onEditHeader,
  acceptance = false,
}: Omit<ReviewConfirmationProps, 'open' | 'completing' | 'onClose'> & { acceptance?: boolean }) {
  if (!document || !draft) return null
  const original = document.original
  const header = { ...original, ...draft.header }
  const completion = reviewCompletionState(document, draft)
  const totals = totalsCheck(original, draft)
  const blocked = saving || readOnly || resultChanged
  const finalDraft = draftForConfirmation(document, draft)
  const rowQuestions = (original.items || []).filter(line =>
    rowHasDataQuestions(
      line,
      finalDraft.items.find(row => row.line_number === line.line_number),
      draft.matching_mode === 'unlinked',
    ),
  )
  const headerLabels = {
    supplier: 'Поставщик',
    document_number: 'Номер накладной',
    document_date: 'Дата накладной',
  }
  const missingHeader = (Object.keys(headerLabels) as Array<keyof typeof headerLabels>).filter(
    field => !String(header[field] ?? '').trim(),
  )
  return (
    <>
      <div className="supply-check__completion-summary">
        <div>
          <strong>Накладная № {header.document_number || 'не указан'}</strong>
          <span>{header.supplier || 'Поставщик не указан'}</span>
        </div>
        <span>
          <CheckCircleOutlined /> {completion.canStart ? 'Товары подтверждены' : 'Проверьте товары'}
        </span>
      </div>
      {!readOnly && (
        <p>
          Подтверждая проверку, вы принимаете данные подтверждённых строк, включая отмеченные
          расхождения.
        </p>
      )}
      {error && (
        <Alert type="error" showIcon message="Не удалось сохранить проверку" description={error} />
      )}
      {resultChanged && (
        <Alert
          type="warning"
          showIcon
          message="Результат разбора обновился"
          description="Вернитесь к накладной и обновите данные перед подтверждением."
        />
      )}
      {!completion.canStart && !readOnly && (
        <Alert type="info" showIcon message="Сначала подтвердите все товары в таблице" />
      )}
      {!!original.recognition_summary?.missing_pages?.length && <Alert type="error" showIcon message="Не все страницы прочитаны" description={`Повторите разбор: страницы ${original.recognition_summary.missing_pages.join(', ')}`} />}
      {missingHeader.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message="Заполните реквизиты накладной"
          description={missingHeader.map(field => headerLabels[field]).join(', ')}
          action={
            <Button size="small" icon={<EditOutlined />} disabled={blocked} onClick={onEditHeader}>
              Исправить
            </Button>
          }
        />
      )}
      {totals && !totals.matches && (
        <Alert
          type="warning"
          showIcon
          message="Сумма строк не совпадает с итогом накладной"
          description={`Сумма строк: ${moneyValue(totals.actual)}. Итог накладной: ${moneyValue(
            totals.expected,
          )}. Исправьте значения перед завершением проверки.`}
        />
      )}
      {rowQuestions.length > 0 && (
        <section className="supply-check__completion-section">
          <h4>
            <ExclamationCircleOutlined /> Осталось проверить строки: {rowQuestions.length}
          </h4>
          <p>
            Сверьте данные отмеченных строк с оригиналом и сохраните решение по каждому вопросу.
          </p>
          <div className="supply-check__completion-cards">
            {rowQuestions.map(line => {
              const row = finalDraft.items.find(item => item.line_number === line.line_number)
              const issues = line.issues.filter(
                (issue, index) =>
                  issue.field !== 'match' &&
                  !issueResolved(line, row, index, draft.matching_mode === 'unlinked'),
              )
              return (
                <div className="supply-check__completion-row" key={line.line_number}>
                  <div>
                    <strong>
                      Строка {line.line_number} · {displayValue(valuesFor(line, row).name)}
                    </strong>
                    {issues.length ? (
                      <ul>
                        {issues.map((issue, index) => (
                          <li key={index}>{issue.message}</li>
                        ))}
                      </ul>
                    ) : (
                      <p>Заполните недостающие данные строки.</p>
                    )}
                  </div>
                  <Button
                    size="small"
                    icon={<ExclamationCircleOutlined />}
                    disabled={blocked}
                    onClick={() => onEditLine(line)}
                  >
                    Проверить строку {line.line_number}
                  </Button>
                </div>
              )
            })}
          </div>
        </section>
      )}
      {completion.canComplete && !readOnly && (
        <Alert
          type="success"
          showIcon
          message="Накладная готова к завершению проверки"
          description={
            acceptance
              ? 'Укажите дату и нажмите «Подтвердить приёмку». Проверка и приёмка сохранятся в ERP.'
              : 'Решения сохраняются автоматически. Завершите проверку основным действием под таблицей.'
          }
        />
      )}
    </>
  )
}
